import { useEffect, useRef, useState } from "react";

export const slugify = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const AdminInput = ({
  id,
  label,
  description,
  required = false,
  error,
  ariaLabel,
  onWritting,
  onChange,
  icon,
  iconType,
  iconPositionRight,
  classname,
  validation,
  ...props
}) => (
  <div className="adminField">
    {label && (
      <label htmlFor={id}>
        {label}
        {required && <span className="requiredMark"> · Obligatorio</span>}
      </label>
    )}
    <input
      {...props}
      id={id}
      required={required}
      aria-label={ariaLabel}
      aria-invalid={Boolean(error)}
      aria-describedby={
        error ? `${id}-error` : description ? `${id}-help` : undefined
      }
      onChange={(event) => {
        onWritting?.(event.target.value);
        onChange?.(event.target.value);
      }}
    />
    {error ? (
      <p id={`${id}-error`} className="fieldError" role="alert">
        {error}
      </p>
    ) : (
      description && (
        <p id={`${id}-help`} className="fieldHelp">
          {description}
        </p>
      )
    )}
  </div>
);

export const AdminTextarea = ({
  id,
  label,
  text,
  description,
  error,
  ...props
}) => (
  <div className="adminField">
    <label htmlFor={id}>{label || text}</label>
    <textarea
      {...props}
      id={id}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : description ? `${id}-help` : undefined}
    />
    {error && (
      <p id={`${id}-error`} className="fieldError" role="alert">
        {error}
      </p>
    )}
    {!error && description && <p id={`${id}-help`} className="fieldHelp">{description}</p>}
  </div>
);

export const SearchMetadata = ({
  expanded = false,
  data,
  nameId,
  slugError,
  onSlugChange = () => null,
  kind = "producto",
}) => {
  let seo = data?.seo;
  if (typeof seo === "string") {
    try {
      seo = JSON.parse(seo);
    } catch {
      seo = {};
    }
  }
  const initial = useRef({
    title: seo?.title || "",
    slug: data?.urlSlug || seo?.keywords || "",
    description: seo?.description || "",
  });
  const detailsRef = useRef(null);
  useEffect(() => {
    if (slugError && detailsRef.current) {
      detailsRef.current.open = true;
      detailsRef.current.querySelector("#seoUrlInput")?.focus();
    }
  }, [slugError]);
  const [preview, setPreview] = useState(initial.current);
  useEffect(() => {
    const nameInput = document.getElementById(nameId);
    const descriptionInput = expanded
      ? document.getElementById("descriptionProduct")
      : null;
    const fields = {
      title: document.getElementById("seoNameInput"),
      slug: document.getElementById("seoUrlInput"),
      description: document.getElementById("seoDescriptionInput"),
    };
    const automatic = Object.fromEntries(
      Object.entries(initial.current).map(([key, value]) => [key, !value]),
    );
    const updatePreview = () =>
      setPreview(
        Object.fromEntries(
          Object.entries(fields).map(([key, field]) => [
            key,
            field?.value || "",
          ]),
        ),
      );
    const sync = () => {
      const name = nameInput?.value || "";
      Object.entries(fields).forEach(([key, field]) => {
        if (field && automatic[key])
          field.value =
            key === "slug"
              ? slugify(name)
              : key === "description"
                ? descriptionInput?.value || name
                : name;
      });
      updatePreview();
    };
    const handlers = Object.entries(fields).map(([key, field]) => {
      const handler = () => {
        automatic[key] = false;
        updatePreview();
      };
      field?.addEventListener("input", handler);
      return () => field?.removeEventListener("input", handler);
    });
    nameInput?.addEventListener("input", sync);
    descriptionInput?.addEventListener("input", sync);
    sync();
    return () => {
      nameInput?.removeEventListener("input", sync);
      descriptionInput?.removeEventListener("input", sync);
      handlers.forEach((remove) => remove());
    };
  }, [nameId, expanded]);
  return (
    <details
      open={expanded || undefined}
      className={`metadataEditor ${expanded ? "metadataExpanded" : ""}`}
      ref={detailsRef}
    >
      <summary>
        <span>Enlace y buscadores</span>
        <span className="fieldHelp">
          Se completa desde el nombre. Personalízalo si lo necesitas.
        </span>
      </summary>
      <div className="metadataContent">
        <AdminInput
          id="seoUrlInput"
          label={
            kind === "categoría"
              ? "Enlace de la categoría"
              : expanded
                ? "Identificador de URL"
                : "Enlace del producto"
          }
          defaultValue={initial.current.slug}
          error={slugError}
          onWritting={onSlugChange}
          description="Usa letras minúsculas, números y guiones. Por ejemplo: hamburguesa-de-la-casa."
        />
        <AdminInput
          id="seoNameInput"
          label={expanded ? "Título de la página" : "Título en los buscadores"}
          description={
            expanded ? "Recomendado: hasta 60 caracteres" : undefined
          }
          defaultValue={initial.current.title}
        />
        <AdminTextarea
          id="seoDescriptionInput"
          label={
            expanded ? "Meta descripción" : "Descripción en los buscadores"
          }
          defaultValue={initial.current.description}
          description={expanded ? "Recomendado: hasta 155 caracteres" : undefined}
          rows={3}
        />
        <div className="searchPreview" aria-label="Vista previa del enlace">
          <span>
            {expanded
              ? "PREVISUALIZACIÓN DEL MOTOR DE BÚSQUEDA"
              : "Vista previa"}
          </span>
          <strong>{preview.title || "Nombre del producto"}</strong>
          <code>
            {expanded
              ? `https://${window.location.hostname.replace(".admin.", ".shop.")}/tienda/`
              : "…/"}
            {preview.slug || "enlace-del-producto"}
          </code>
          <p>{preview.description || "La descripción aparecerá aquí."}</p>
        </div>
      </div>
    </details>
  );
};

export const validateRequiredInputs = () => {
  const root = document.querySelector(".floatPanel") || document;
  const invalid = Array.from(
    root.querySelectorAll(
      "input[required],textarea[required],select[required]",
    ),
  ).find((field) => !field.checkValidity() || !field.value.trim());
  if (!invalid) return true;
  invalid.focus();
  invalid.reportValidity();
  return false;
};
